import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  ClipboardCheck, Layers3, CalendarClock, FileText,
  CalendarRange, ListChecks, CheckCircle2, Circle, Clock, User, Calendar, Users,
  CalendarDays, MessageSquare, ShieldCheck, Ban,
} from 'lucide-react';
import studyBuilderService from '../../services/studyBuilderService';
import { getStudyDraftReviewContext } from '../../services/reviewWorkflowApi';
import SchedulePreviewModal from './SchedulePreviewModal';
import ScheduleReviewCard, { SectionLabel, Chip, Muted } from './ScheduleReviewCard';

/**
 * Dedicated, read-only review of a study draft's design (FECOA review workflow).
 *
 * A purpose-built mirror of the design tool: it renders the full design as a
 * polished top-to-bottom document (status, readiness, arms, and every schedule
 * with its surveys, reminders, site type, windows, and stages). There are no
 * edit controls by construction. The Approve/Deny bar is supplied by ReviewFrame.
 */

const STATUS_META = {
  draft: { label: 'Draft', cls: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200', Icon: Clock },
  in_review: { label: 'Pending Review', cls: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200', Icon: Clock },
  approved: { label: 'Approved', cls: 'bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-200', Icon: CheckCircle2 },
  denied: { label: 'Needs Revision', cls: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200', Icon: MessageSquare },
  published: { label: 'Published', cls: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200', Icon: CheckCircle2 },
};

const fmtDateTime = (v) => {
  if (!v) return null;
  try { return new Date(v).toLocaleString(); } catch { return v; }
};

const StatTile = ({ icon: Icon, label, value }) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-4 flex items-center gap-3">
    <div className="bg-primary-50 dark:bg-primary-900/30 p-2.5 rounded-lg flex-shrink-0">
      <Icon className="w-5 h-5 text-primary-600 dark:text-primary-300" />
    </div>
    <div className="min-w-0">
      <div className="text-lg font-bold text-gray-900 dark:text-gray-100 leading-tight truncate">{value}</div>
      <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
    </div>
  </div>
);

const StudyDesignReview = () => {
  const { studyName } = useParams();
  const [draft, setDraft] = useState(null);
  const [surveys, setSurveys] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [reviewCtx, setReviewCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const drafts = (await studyBuilderService.getStudyDrafts()) || [];
        const found = drafts.find((d) => d.name === studyName) || null;
        if (active) setDraft(found);
        if (found) {
          localStorage.setItem('currentStudyDraftId', String(found.id));
          const [surveyData, templateData, ctx] = await Promise.all([
            studyBuilderService.getSurveysByStudyName(found.name).catch(() => null),
            studyBuilderService.getReminderTemplates(found.id).catch(() => null),
            getStudyDraftReviewContext(found.id).catch(() => null),
          ]);
          if (active) {
            setReminders(templateData?.results || templateData || []);
            setReviewCtx(ctx);
          }
          // The study-name lookup misses surveys when the draft name has
          // drifted from the Study title — resolve unnamed ids individually,
          // inside the loading window so a bare "Survey #id" never flashes.
          const loaded = surveyData?.surveys || [];
          const draftSchedules = Array.isArray(found.data?.schedules) ? found.data.schedules : [];
          const extras = await studyBuilderService
            .resolveMissingScheduleSurveys(draftSchedules, loaded)
            .catch(() => []);
          if (active) {
            setSurveys(extras.length > 0 ? [...loaded, ...extras] : loaded);
          }
        }
      } catch (e) {
        console.error('Error loading study design for review:', e);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [studyName]);

  const surveyName = useMemo(() => (id) => {
    const found = surveys.find((s) => s.id === parseInt(id, 10));
    return found ? found.name : `Survey #${id}`;
  }, [surveys]);
  const reminderName = useMemo(() => (id) => {
    const found = reminders.find((r) => r.id === parseInt(id, 10));
    return found ? found.name : `Reminder #${id}`;
  }, [reminders]);

  const data = draft?.data || {};
  const arms = Array.isArray(data.arms) ? data.arms : [];
  const schedules = Array.isArray(data.schedules) ? data.schedules : [];
  const startDay = schedules[0]?.studyStartDay;
  const status = STATUS_META[draft?.review_status] || STATUS_META.draft;
  const StatusIcon = status.Icon;

  const readiness = [
    { label: 'Study start day set', done: startDay !== undefined },
    { label: 'Arms configured', done: arms.length > 0 },
    { label: 'Schedules configured', done: schedules.length > 0 },
    { label: 'Surveys assigned', done: schedules.some((s) => Array.isArray(s?.standardSurveys) && s.standardSurveys.length > 0) },
  ];
  const doneCount = readiness.filter((r) => r.done).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary-600 border-t-transparent" />
      </div>
    );
  }

  if (!draft) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center px-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-10 text-center text-gray-500 dark:text-gray-400">
          Could not find this study draft.
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto space-y-6">

        {/* Hero */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary-600 to-primary-700 dark:from-primary-700 dark:to-primary-900 text-white p-6 shadow-lg">
          <div className="absolute -right-8 -top-8 opacity-10">
            <ClipboardCheck className="w-40 h-40" />
          </div>
          <div className="relative flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-xs font-medium uppercase tracking-wider text-white/70 mb-1">Study Design Review · read-only</p>
              <h1 className="text-2xl sm:text-3xl font-bold truncate">{draft.name}</h1>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm text-white/80">
                {draft.submitted_by_email && (
                  <span className="flex items-center gap-1.5"><User className="w-4 h-4" /> {draft.submitted_by_email}</span>
                )}
                {draft.submitted_at && (
                  <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4" /> {fmtDateTime(draft.submitted_at)}</span>
                )}
              </div>
            </div>
            <div className="flex-shrink-0 flex flex-col items-end gap-1.5">
              <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${status.cls}`}>
                <StatusIcon className="w-3.5 h-3.5" /> {status.label}
              </span>
              {reviewCtx?.assessments?.total > 0 && reviewCtx.assessments.all_approved && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-200">
                  <ShieldCheck className="w-3.5 h-3.5" /> All Assessments Approved
                </span>
              )}
              {reviewCtx?.prior_denial_count > 0 && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200">
                  <Ban className="w-3.5 h-3.5" /> {reviewCtx.prior_denial_count} prior denial{reviewCtx.prior_denial_count === 1 ? '' : 's'}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Submitter's note */}
        {reviewCtx?.submission_note && (
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-5">
            <SectionLabel>Submitter&apos;s Note</SectionLabel>
            <div className="flex gap-2">
              <MessageSquare className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-gray-700 dark:text-gray-200 italic">“{reviewCtx.submission_note}”</p>
            </div>
          </div>
        )}

        {/* Approval message */}
        {reviewCtx?.approval && (
          <div className="bg-teal-50/60 dark:bg-teal-900/15 rounded-2xl border border-teal-200 dark:border-teal-800/50 p-5">
            <SectionLabel>Approval</SectionLabel>
            <div className="flex items-center gap-2 text-teal-700 dark:text-teal-300 font-medium text-sm">
              <ShieldCheck className="w-3.5 h-3.5 flex-shrink-0" />
              Approved by {reviewCtx.approval.decided_by_name}
              {reviewCtx.approval.decided_at && (
                <span className="text-teal-500/80 dark:text-teal-300/70 font-normal">· {fmtDateTime(reviewCtx.approval.decided_at)}</span>
              )}
            </div>
            {reviewCtx.approval.message && (
              <p className="mt-1 ml-5 text-sm text-teal-700/90 dark:text-teal-200/90 italic">“{reviewCtx.approval.message}”</p>
            )}
          </div>
        )}

        {/* Previous denial history */}
        {reviewCtx?.denial_history?.length > 0 && (
          <div className="bg-red-50/60 dark:bg-red-900/15 rounded-2xl border border-red-200 dark:border-red-800/50 p-5">
            <SectionLabel>Previous Denial History</SectionLabel>
            <ul className="space-y-3">
              {reviewCtx.denial_history.map((d, i) => (
                <li key={i} className="text-sm">
                  <div className="flex items-center gap-2 text-red-700 dark:text-red-300 font-medium">
                    <Ban className="w-3.5 h-3.5 flex-shrink-0" />
                    Denied by {d.decided_by_name}
                    {d.decided_at && <span className="text-red-500/80 dark:text-red-300/70 font-normal">· {fmtDateTime(d.decided_at)}</span>}
                  </div>
                  {d.reason && <p className="mt-0.5 ml-5 text-red-700/90 dark:text-red-200/90 italic">“{d.reason}”</p>}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Stat tiles */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatTile icon={CalendarRange} label="Study start" value={startDay !== undefined ? `Day ${startDay}` : '—'} />
          <StatTile icon={Users} label={arms.length === 1 ? 'Arm' : 'Arms'} value={arms.length} />
          <StatTile icon={CalendarClock} label={schedules.length === 1 ? 'Schedule' : 'Schedules'} value={schedules.length} />
          <StatTile icon={FileText} label={surveys.length === 1 ? 'Survey' : 'Surveys'} value={surveys.length} />
        </div>

        {/* Readiness */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <ListChecks className="w-5 h-5 text-primary-600 dark:text-primary-300" />
              <h2 className="font-semibold text-gray-900 dark:text-gray-100">Readiness</h2>
            </div>
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{doneCount}/{readiness.length} complete</span>
          </div>
          <div className="grid sm:grid-cols-2 gap-2">
            {readiness.map((r, i) => (
              <div key={i} className="flex items-center gap-2 text-sm">
                {r.done
                  ? <CheckCircle2 className="w-4 h-4 text-teal-500 flex-shrink-0" />
                  : <Circle className="w-4 h-4 text-gray-300 dark:text-gray-600 flex-shrink-0" />}
                <span className={r.done ? 'text-gray-700 dark:text-gray-200' : 'text-gray-400 dark:text-gray-500'}>{r.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Arms */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-5">
          <div className="flex items-center gap-2 mb-3">
            <Layers3 className="w-5 h-5 text-primary-600 dark:text-primary-300" />
            <h2 className="font-semibold text-gray-900 dark:text-gray-100">Arms</h2>
          </div>
          {arms.length > 0
            ? <div className="flex flex-wrap gap-2">{arms.map((a, i) => <Chip key={i}>{a}</Chip>)}</div>
            : <Muted>No arms configured</Muted>}
        </div>

        {/* Schedules */}
        <div>
          <div className="flex items-center gap-2 mb-3 px-1">
            <CalendarClock className="w-5 h-5 text-primary-600 dark:text-primary-300" />
            <h2 className="font-semibold text-gray-900 dark:text-gray-100">Schedules</h2>
            <span className="text-xs text-gray-400">({schedules.length})</span>
            {schedules.length > 0 && (
              <button
                onClick={() => setPreviewOpen(true)}
                className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary-700 dark:text-primary-200 bg-primary-50 dark:bg-primary-900/30 hover:bg-primary-100 dark:hover:bg-primary-900/50 rounded-lg transition-colors"
                title="Visualize this schedule against a sample enrollment date"
              >
                <CalendarDays className="w-4 h-4" />
                <span>Preview calendar</span>
              </button>
            )}
          </div>
          {schedules.length > 0 ? (
            <div className="space-y-4">
              {schedules.map((s, i) => (
                <ScheduleReviewCard key={i} schedule={s} surveyName={surveyName} reminderName={reminderName} />
              ))}
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-8 text-center">
              <Muted>No schedules configured</Muted>
            </div>
          )}
        </div>

      </div>

      <SchedulePreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        schedules={schedules}
        studyStartDay={startDay}
      />
    </div>
  );
};

export default StudyDesignReview;
